import {
  DeletePresetFilter,
  GetPresetsFilters,
  SavePresetFilter,
  UpdatePresetFilter,
} from '@/apis/shipment';
import {
  DeleteIcon,
  FileRefreshIcon,
  TaskListIcon,
  ChevronDownIcon,
} from '@/assets/icons/icons';
import { useToken } from '@/common/helper';
import { getTeamsDDLOFFeature } from '@/common/utils';
import { CustomShipmentText, TruncateWithTooltip } from '@/components';
import useUnauthorize from '@/hooks/useUnauthorize';
import {
  Checkbox,
  Divider,
  Dropdown,
  Flex,
  Input,
  message,
  Spin,
  Tooltip,
} from 'antd';
import { memo, useCallback, useEffect, useState, type FC } from 'react';
import {
  AppliedFilter,
  FilterArrayItem,
} from '@/pages/Shipments/ControlTower/types';
import { CustomDeleteModal } from '@/pages/Modals';
import { HeaderItem, PermissionsObject, TabItem } from '../types';
import type { ErrorLike } from '@/apis/types';

interface PresetsTeams {
  id?: string;
  name?: string;
  color?: string;
}

export interface PresetInterface {
  id?: string;
  name: string;
  presets: HeaderItem[];
  last_used?: boolean;
  filters?: {
    filterArray: FilterArrayItem[];
    appliedFilters: AppliedFilter[];
  };
  teams?: PresetsTeams[];
  updated_at?: string;
  updated_by?: string | number;
  tab_presets?: TabItem[];
}

const toErrorLike = (error: unknown): ErrorLike | undefined => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof error.status === 'number'
  ) {
    return { status: error.status };
  }

  return undefined;
};

interface PresetsDropdownProps {
  onPresetApplied?: (preset: PresetInterface) => void;
  isReportLoading?: boolean;
  permissions?: PermissionsObject;
  tabs?: TabItem[];
  /**
   * Optional override for the tabs payload used on save/update.
   * Lets the parent flush in-session custom-column drafts (including rows
   * no longer in the current search result) into the API request.
   */
  getTabsForSave?: () => TabItem[];
  sheetTitle?: string;
}

interface ApiResponse<T> {
  data?: {
    data?: T;
  };
}

const PresetsDropdown: FC<PresetsDropdownProps> = ({
  onPresetApplied,
  isReportLoading = false,
  permissions,
  tabs,
  getTabsForSave,
  sheetTitle,
}) => {
  const { unauthorize } = useUnauthorize();
  const { token } = useToken();
  const [open, setOpen] = useState(false);
  const [isRefreshConfirmVisible, setIsRefreshConfirmVisible] = useState(false);
  const [presetToUpdate, setPresetToUpdate] = useState<PresetInterface | null>(
    null
  );
  const [actionType, setActionType] = useState<string>('update');
  const [presets, setPresets] = useState<PresetInterface[]>([]);
  const [loading, setLoading] = useState(false);

  const tabsForSave = () => getTabsForSave?.() ?? tabs;
  const [presetClickLocked, setPresetClickLocked] = useState(false);
  const [presetApplyingId, setPresetApplyingId] = useState<string | null>(null);
  const [currentPreset, setCurrentPreset] = useState<PresetInterface>();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [allTeams, setAllTeams] = useState<PresetsTeams[]>([]);
  const [showSave, setShowSave] = useState<boolean>(false);
  const [showNameInput, setShowNameInput] = useState<boolean>(false);
  const [nameInput, setNameInput] = useState<string>('');

  const resetPresetLoadingState = () => {
    setPresetClickLocked(false);
    setPresetApplyingId(null);
  };

  useEffect(() => {
    if (!isReportLoading) {
      resetPresetLoadingState();
    }
  }, [isReportLoading]);

  const handleRequestRefresh = (
    preset: PresetInterface,
    actionType: string
  ) => {
    setActionType(actionType);
    setPresetToUpdate(preset);
    setIsRefreshConfirmVisible(true);
  };

  const handleConfirmRefresh = () => {
    if (!presetToUpdate?.id) {
      setIsRefreshConfirmVisible(false);
      return;
    }

    setIsRefreshConfirmVisible(false);
    setPresetClickLocked(true);
    setPresetApplyingId(presetToUpdate.id);
    if (actionType === 'Update') {
      handleUpdatePreset(presetToUpdate.id);
    } else {
      handleDeletePreset(presetToUpdate.id);
    }
  };

  const handleCancelRefresh = () => {
    setIsRefreshConfirmVisible(false);
    setPresetToUpdate(null);
  };

  const handleGetPresetsFilters = useCallback(() => {
    GetPresetsFilters(token ?? '', 'presets')
      .then((res) => {
        const response = res as ApiResponse<PresetInterface[]>;
        const additionalOptions = response?.data?.data || [];
        setPresets(additionalOptions);
        setOpen(false);
      })
      .catch((error: unknown) => {
        unauthorize(toErrorLike(error));
      });
  }, [token, unauthorize]);

  useEffect(() => {
    void handleGetPresetsFilters();
  }, [handleGetPresetsFilters]);

  const handleCheckboxChange = (checked: boolean, id: string) => {
    setSelectedItems((prev) =>
      checked ? [...prev, id] : prev.filter((item) => item !== id)
    );
  };

  useEffect(() => {
    void getTeamsDDLOFFeature(token ?? '', 'ReportShipmentTeams')
      .then((val) => {
        if (val && val?.length > 0) {
          setAllTeams(val);
        }
      })
      .catch((error: unknown) => {
        unauthorize(toErrorLike(error));
      });
  }, [token, unauthorize]);

  const singleTeam = allTeams?.length === 0;

  useEffect(() => {
    setShowNameInput(false);
    setNameInput(sheetTitle || '');
    setShowSave(false);
    setSelectedItems([]);
  }, [open, sheetTitle]);

  const handleSavePreset = () => {
    if (!nameInput) return;
    setLoading(true);

    const model = {
      type: 'presets',
      teams: selectedItems,
      name: nameInput,
      tab_presets: tabsForSave()?.map(
        ({
          id: _id,
          created_at: _created_at,
          created_by: _created_by,
          updated_at: _updated_at,
          updated_by: _updated_by,
          preset: _preset,
          ...rest
        }) => rest
      ),
      report_name: sheetTitle,
    };

    SavePresetFilter(token ?? '', model)
      .then((res) => {
        const response = res?.data?.data;
        if (response?.is_success) {
          message.success(response?.message || 'Preset saved successfully');
          void handleGetPresetsFilters();
          setShowNameInput(false);
          setNameInput(sheetTitle || '');
          setShowSave(false);
          setSelectedItems([]);
          setLoading(false);
        } else {
          setLoading(false);
          message.error(response?.message || 'Failed to save preset');
        }
      })
      .catch((error: unknown) => {
        unauthorize(toErrorLike(error));
      });
  };

  const handleDeletePreset = (id?: string) => {
    if (!id) return;
    DeletePresetFilter(id, token ?? '')
      .then((res) => {
        const response = res?.data?.data;
        if (response?.is_success) {
          message.success(response?.message || 'Preset deleted successfully');
          void handleGetPresetsFilters();
        } else {
          message.error(response?.message || 'Failed to delete preset');
        }
      })
      .catch((error: unknown) => {
        unauthorize(toErrorLike(error));
      })
      .finally(() => {
        resetPresetLoadingState();
      });
  };

  const handleUpdatePreset = (id?: string) => {
    if (!id) return;
    const model = {
      type: 'presets',
      teams: currentPreset?.teams
        ?.filter((team) => !!team?.id)
        ?.map((team) => team.id),
      name: currentPreset?.name,
      tab_presets: tabsForSave()?.map(
        ({
          id: _id,
          created_at: _created_at,
          created_by: _created_by,
          updated_at: _updated_at,
          updated_by: _updated_by,
          preset: _preset,
          ...rest
        }) => rest
      ),
      report_name: sheetTitle,
    };
    UpdatePresetFilter(id, model, token ?? '')
      .then((res) => {
        const response = res?.data?.data;
        if (response?.is_success) {
          message.success(response?.message || 'Preset updated successfully');
          void handleGetPresetsFilters();
        } else {
          message.error(response?.message || 'Failed to update preset');
        }
      })
      .catch((error: unknown) => {
        unauthorize(toErrorLike(error));
      })
      .finally(() => {
        resetPresetLoadingState();
      });
  };

  const isGloballyDisabled = () => isReportLoading || presetClickLocked;

  const menu = (
    <div className='preset-dropdown-card'>
      <div className='px-4'>
        <div className='zl-heading-12 gray-5e report-preset-heading'>
          Presets
        </div>

        <div className='zl-para-10 gray-5e report-preset-description'>
          Save filters and columns to reuse later
        </div>
      </div>
      <Divider className='report-preset-divider' />

      {presets
        ?.filter((preset) => preset.last_used === true)
        ?.map((preset) => {
          const isGlobalDisabled = isReportLoading || presetClickLocked;
          const isThisActive = presetApplyingId === preset.id;

          const handleLastUsedClick = () => {
            if (isGlobalDisabled || isThisActive) return;

            setPresetClickLocked(true);
            setPresetApplyingId(preset.id || null);

            onPresetApplied?.(preset);
            setCurrentPreset(preset);
          };

          return (
            <div key={`last-used-${preset.id}`}>
              <div
                className={`zl-para-400-12 px-4 py-1 report-preset-row${isGlobalDisabled ? ' is-disabled' : ''}${isThisActive && isGlobalDisabled ? ' is-loading' : ''}`}
                onClick={handleLastUsedClick}
              >
                {isThisActive && isGlobalDisabled && (
                  <Spin size='small' className='report-preset-spinner' />
                )}
                Last used
              </div>
              <Divider className='report-preset-divider' />
            </div>
          );
        })}

      {presets?.length > 0 ? (
        <div className='scroll-saved-filter'>
          {singleTeam && (
            <div className='zl-para-400-12 color-5e px-4 report-preset-section-title'>
              Saved presets
            </div>
          )}

          {Object.values(
            presets?.reduce<
              Record<string, PresetsTeams & { presets: PresetInterface[] }>
            >((acc, preset) => {
              preset?.teams?.forEach?.((team) => {
                if (!team?.id) return;
                if (!acc[team.id]) {
                  acc[team.id] = { ...team, presets: [] };
                }
                acc[team.id].presets.push(preset);
              });
              return acc;
            }, {})
          )?.map((team, index, arr) => (
            <div
              key={team?.id}
              className={
                index !== arr.length - 1 ? 'report-preset-team' : undefined
              }
            >
              {/* Team header */}
              {!singleTeam && (
                <Flex align='center' justify='space-between' className='px-4'>
                  <p className='zl-para-400-12 color-5e'>{team?.name}</p>
                  <svg className='team-color-circle' aria-hidden='true'>
                    <circle cx='50%' cy='50%' r='50%' fill={team?.color} />
                  </svg>
                </Flex>
              )}

              {team?.presets?.map((preset) => {
                const isDisabled = isGloballyDisabled();
                const isThisActive = presetApplyingId === preset.id;
                const isActivePreset = currentPreset?.id === preset?.id;

                const handlePresetClick = () => {
                  if (isDisabled || isActivePreset) return;

                  setPresetClickLocked(true);
                  setPresetApplyingId(preset.id || null);

                  onPresetApplied?.(preset);
                  setCurrentPreset(preset);
                };

                return (
                  <div
                    key={`${team?.id}-${preset?.id}`}
                    className={`zl-para-400-12 flex-center btw parent-hover-show py-1-5 px-4 report-preset-row${isDisabled ? ' is-disabled' : ''}${isThisActive && isDisabled ? ' is-loading' : ''}`}
                    onClick={handlePresetClick}
                  >
                    <TruncateWithTooltip
                      text={String(preset?.name)}
                      max={20}
                      render={(t) => (
                        <span className='w-fit'>
                          <CustomShipmentText text={t} lineHeight='20px' />
                        </span>
                      )}
                    />

                    <Flex align='center' gap={8}>
                      {isThisActive && isDisabled && <Spin size='small' />}

                      {isActivePreset && !isDisabled && (
                        <Tooltip
                          title={
                            <div>
                              <div className='font-10'>
                                <strong>Update {preset?.name || ''}</strong>
                              </div>
                              <div className='font-10'>
                                <i>Last updated</i>
                              </div>
                              <div className='font-10'>
                                {preset?.updated_by || ''}{' '}
                                {preset?.updated_at
                                  ? preset?.updated_at
                                      ?.split(' ')[0]
                                      .replace(/-/g, '/')
                                  : ''}
                              </div>
                            </div>
                          }
                        >
                          {(permissions?.is_create ||
                            permissions?.is_update) && (
                            <span
                              className='delete-icon report-preset-action'
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isGloballyDisabled()) return;
                                handleRequestRefresh(preset, 'Update');
                              }}
                            >
                              <FileRefreshIcon />
                            </span>
                          )}
                        </Tooltip>
                      )}

                      {!isDisabled && permissions?.is_delete && (
                        <span
                          className='delete-icon report-preset-action'
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isGloballyDisabled()) return;
                            handleRequestRefresh(preset, 'Delete');
                          }}
                        >
                          <DeleteIcon />
                        </span>
                      )}
                    </Flex>
                  </div>
                );
              })}

              {index !== arr?.length - 1 && (
                <Divider className='report-preset-divider report-preset-divider-flush' />
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className='disabled-row flex-center x mt-3 mb-2'>no presets</p>
      )}

      {/* team to save presets START */}
      {allTeams?.length > 0 && showSave ? (
        <>
          <Divider className='report-preset-divider' />
          <p className='zl-heading-12 gray-5e py-1 px-4'>Make visible to ...</p>
          <div className='scroll-saved-filter'>
            {allTeams?.map((option) => (
              <div
                key={option.id}
                className='for-dropdown-colors flex-center y py-1-5 px-4'
              >
                <Checkbox
                  checked={
                    option.id ? selectedItems.includes(option.id) : false
                  }
                  onChange={(e) => {
                    if (option.id) {
                      handleCheckboxChange(e.target.checked, option.id);
                    }
                  }}
                  className='home-map-filter-check-box font-size-12'
                >
                  <TruncateWithTooltip
                    text={String(option?.name || '')}
                    max={12}
                    render={(t) => (
                      <span className='w-fit'>
                        <span>{t}</span>
                      </span>
                    )}
                  />
                </Checkbox>
                <svg
                  className='team-color-circle is-home-map-filter'
                  aria-hidden='true'
                >
                  <circle cx='50%' cy='50%' r='50%' fill={option?.color} />
                </svg>
              </div>
            ))}
          </div>
        </>
      ) : null}
      {/* team to save presets  END */}

      <Divider className='report-preset-divider report-preset-divider-footer' />
      <div className='px-4'>
        {showNameInput ? (
          <>
            {showSave ? (
              <button
                onClick={() => handleSavePreset()}
                disabled={selectedItems?.length < 1}
                className={`zl-btn secondary radius-8 w-full ${selectedItems?.length < 1 && 'disabled'}`}
              >
                {loading && <Spin size='small' />}
                Save
              </button>
            ) : (
              <Flex vertical gap={4}>
                <Input
                  className='custom-editable-preset-name-input'
                  autoFocus
                  allowClear
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  size='small'
                  style={{ width: '100%' }}
                />
                {singleTeam ? (
                  <button
                    onClick={() => handleSavePreset()}
                    className='zl-btn secondary radius-8 w-full'
                  >
                    Save
                  </button>
                ) : (
                  <button
                    onClick={() => setShowSave(true)}
                    disabled={!nameInput}
                    className={`zl-btn secondary radius-8 w-full ${!nameInput && 'disabled'}`}
                  >
                    Next
                  </button>
                )}
              </Flex>
            )}
          </>
        ) : (
          <>
            {permissions?.is_create && (
              <button
                onClick={() => setShowNameInput(true)}
                className='zl-btn secondary radius-8 w-full'
              >
                Save current selection
              </button>
            )}
          </>
        )}
      </div>
      <CustomDeleteModal
        visible={isRefreshConfirmVisible}
        onClose={handleCancelRefresh}
        onConfirm={handleConfirmRefresh}
        title={`${actionType} ${presetToUpdate?.name || ''}`}
        message={`${actionType} existing preset with current filters and columns for all users. This cannot be undone.`}
        cancelText='Cancel'
        confirmText={`${actionType} this preset`}
        confirmColor='var(--text-primary)'
      />
    </div>
  );

  const presetsDropdown = (
    <Dropdown
      dropdownRender={() => menu}
      trigger={['click']}
      placement='bottomLeft'
      open={open}
      onOpenChange={(flag) => setOpen(flag)}
    >
      <span className='cursor-pointer'>
        <button
          type='button'
          className='zl-btn report-presets-trigger rounded-full flex-center y'
        >
          <TaskListIcon /> Presets <ChevronDownIcon />
        </button>
      </span>
    </Dropdown>
  );

  return presetsDropdown;
};

export default memo(PresetsDropdown);
